import { useState } from "react";
import {
  AlertCircle,
  CalendarClock,
  ChevronDown,
  Info,
  Lock,
  LockOpen,
  RefreshCw,
  RotateCcw,
  ShieldAlert,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/cn";
import { useAuthStore } from "@/store/useAuthStore";
import { useForceUnlock, useRunNowJob, useSetJobEnabled, useSyncStatus } from "@/hooks/useSyncCenter";
import type { SyncFactualFlow, SyncJob, SyncJobState, SyncRunLogEntry } from "@/api/types";

const GLOBAL_LOCK_KEY = "global_crm_write";

export function SyncCenterPage() {
  const perms = useAuthStore((s) => s.permissions);
  const canView = !!perms?.canViewSyncCenter;
  const canControl = !!perms?.canRunSyncApply; // super_admin (enable/disable)
  const canForce = !!perms?.canForceUnlock; // super_admin (force-unlock)

  const statusQ = useSyncStatus(canView);
  const setEnabled = useSetJobEnabled();
  const forceUnlock = useForceUnlock();
  const runNow = useRunNowJob();

  const [unlockOpen, setUnlockOpen] = useState(false);
  const [reason, setReason] = useState("");
  // AD-1: inert jobs-board (оркестратор деплой-гейтнутий) — legacy-секція, згорнута.
  const [legacyOpen, setLegacyOpen] = useState(false);

  if (!canView) {
    return (
      <div className="p-4 md:p-6">
        <div className="mx-auto w-full max-w-[640px]">
          <div className="rounded-lg border bg-muted/30 p-8 text-center">
            <ShieldAlert className="mx-auto mb-2 h-5 w-5 text-muted-foreground" />
            <p className="text-sm font-medium">Немає доступу до Синхро-центру</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Розділ доступний адміністраторам.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const data = statusQ.data;
  const lock = data?.lock;
  const factual = data?.factual;

  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto w-full max-w-[1100px] space-y-5">
        <div className="flex items-end justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Синхро-центр</h1>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Фактичний стан синхронізації з BI/1С, журнал запусків і блокування запису.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={() => void statusQ.refetch()}
            disabled={statusQ.isFetching}
          >
            <RefreshCw className={cn("h-3.5 w-3.5", statusQ.isFetching && "animate-spin")} />
            Оновити
          </Button>
        </div>

        {/* Writer lock strip */}
        {lock && (
          <div
            className={cn(
              "flex flex-wrap items-center gap-3 rounded-lg border px-4 py-3",
              lock.held
                ? "border-warning-foreground/30 bg-warning"
                : "border-border bg-muted/30",
            )}
          >
            {lock.held ? (
              <Lock className="h-4 w-4 text-warning-foreground" />
            ) : (
              <LockOpen className="h-4 w-4 text-muted-foreground" />
            )}
            <div className="text-sm">
              <span className="font-medium">Запис-блокування: </span>
              {lock.held ? (
                <span>
                  утримується{lock.lockOwner ? ` (${lock.lockOwner})` : ""}
                  {lock.expiresAt ? ` · до ${lock.expiresAt} UTC` : ""}
                </span>
              ) : (
                <span className="text-muted-foreground">вільне</span>
              )}
            </div>
            <div className="ml-auto flex items-center gap-2">
              {canForce && lock.held && !unlockOpen && (
                <Button size="sm" variant="destructive" onClick={() => setUnlockOpen(true)}>
                  Примусово розблокувати
                </Button>
              )}
            </div>
            {canForce && lock.held && unlockOpen && (
              <div className="flex w-full flex-wrap items-center gap-2 border-t pt-3">
                <Input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Причина (обов'язково, аудиториться)…"
                  className="h-8 max-w-md"
                  aria-label="Причина розблокування"
                />
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={reason.trim() === "" || forceUnlock.isPending}
                  onClick={() =>
                    forceUnlock.mutate(
                      { key: GLOBAL_LOCK_KEY, reason: reason.trim() },
                      { onSuccess: () => { setUnlockOpen(false); setReason(""); } },
                    )
                  }
                >
                  Підтвердити
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setUnlockOpen(false); setReason(""); }}>
                  Скасувати
                </Button>
              </div>
            )}
          </div>
        )}

        {/* AD-1 (AC-AD-SYNC-TRUTH): фактична синхронізація — read-only над CSyncAudit */}
        <section className="space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Фактична синхронізація
          </h2>
          {statusQ.isPending ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => <Skeleton key={i} className="h-20" />)}
            </div>
          ) : statusQ.isError ? (
            <ErrorPanel
              message={(statusQ.error as Error)?.message ?? "Не вдалося завантажити"}
              onRetry={() => void statusQ.refetch()}
            />
          ) : factual ? (
            <>
              <div className="flex items-start gap-2 rounded-md border border-warning-foreground/20 bg-warning px-3 py-2 text-xs text-warning-foreground">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{factual.schedulerNote}</span>
              </div>
              <div className="grid gap-2.5 sm:grid-cols-2">
                {factual.flows.map((flow) => (
                  <FactualFlowCard key={flow.key} flow={flow} />
                ))}
              </div>
              <p className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
                <CalendarClock className="mt-0.5 h-3 w-3 shrink-0" />
                {factual.unjournaledNote}
              </p>
            </>
          ) : (
            <div className="rounded-lg border bg-muted/30 p-6 text-center text-sm text-muted-foreground">
              Дані про фактичну синхронізацію недоступні.
            </div>
          )}
        </section>

        {/* Legacy: inert jobs-board оркестратора (деплой-гейт, Stage-6) — згорнуто */}
        {data && data.jobs.length > 0 && (
          <section className="space-y-2">
            <button
              type="button"
              onClick={() => setLegacyOpen((v) => !v)}
              aria-expanded={legacyOpen}
              className="flex w-full items-center gap-2 rounded-lg border bg-muted/30 px-4 py-3 text-left text-sm hover:bg-muted/50"
            >
              <ChevronDown
                className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", !legacyOpen && "-rotate-90")}
              />
              <span className="font-medium">Планувальник (оркестратор)</span>
              <span className="text-xs text-muted-foreground">
                не активний — запуски виконує системний розклад вище · {data.jobs.length} задач у реєстрі
              </span>
            </button>
            {legacyOpen && (
              <div className="space-y-2.5">
                {data.jobs.map((job) => (
                  <JobCard
                    key={job.jobId}
                    job={job}
                    canControl={canControl}
                    busy={setEnabled.isPending}
                    runBusy={runNow.isPending}
                    onSetOverride={(override) => setEnabled.mutate({ jobId: job.jobId, override })}
                    onRunNow={(reason) =>
                      runNow.mutateAsync({ jobId: job.jobId, mode: job.mode, reason })
                    }
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {/* Run log */}
        <section className="space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Журнал запусків
          </h2>
          {statusQ.isPending ? (
            <Skeleton className="h-40" />
          ) : data && data.runLog.length > 0 ? (
            <RunLogTable rows={data.runLog} />
          ) : (
            <div className="rounded-lg border bg-muted/30 p-6 text-center text-sm text-muted-foreground">
              Журнал порожній.
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function JobCard({
  job,
  canControl,
  busy,
  runBusy,
  onSetOverride,
  onRunNow,
}: {
  job: SyncJob;
  canControl: boolean;
  busy: boolean;
  runBusy: boolean;
  onSetOverride: (override: "inherit" | "disabled") => void;
  onRunNow: (reason: string) => Promise<unknown>;
}) {
  const blocked = job.enabledOverride === "disabled";
  const [runOpen, setRunOpen] = useState(false);
  const [runReason, setRunReason] = useState("");
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium">{job.jobId}</span>
            <Badge variant={job.effectiveEnabled ? "success" : "muted"}>
              {job.effectiveEnabled ? "активна" : "не активна (деплой-гейт)"}
            </Badge>
            {blocked && <Badge variant="destructive">override: disabled</Badge>}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {job.mode} · {job.cadence}
            {job.dependencies.length > 0 && <> · залежить: {job.dependencies.join(", ")}</>}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {/* Dry-run (wired, super_admin). "Запустити (live)" = Stage 6 cutover. */}
          {canControl && (
            <Button size="sm" variant="outline" disabled={runBusy} onClick={() => setRunOpen((v) => !v)}>
              Dry-run
            </Button>
          )}
          <Button size="sm" variant="ghost" disabled title="Доступно після cutover (Stage 6)">
            Запустити (live)
          </Button>
          {canControl &&
            (blocked ? (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => onSetOverride("inherit")}>
                Розблокувати
              </Button>
            ) : (
              <Button size="sm" variant="destructive" disabled={busy} onClick={() => onSetOverride("disabled")}>
                Заблокувати
              </Button>
            ))}
        </div>
      </div>

      {canControl && runOpen && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t pt-3">
          <Input
            value={runReason}
            onChange={(e) => setRunReason(e.target.value)}
            placeholder="Причина dry-run (обов'язково, аудиториться)…"
            className="h-8 max-w-md"
            aria-label="Причина dry-run"
          />
          <Button
            size="sm"
            disabled={runReason.trim() === "" || runBusy}
            onClick={() =>
              onRunNow(runReason.trim()).then(() => {
                setRunOpen(false);
                setRunReason("");
              })
            }
          >
            Запустити dry-run
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { setRunOpen(false); setRunReason(""); }}>
            Скасувати
          </Button>
          <span className="text-[11px] text-muted-foreground">
            Поставить run-request у чергу; оркестратор виконає dry-run через повний lock/gate/audit.
          </span>
        </div>
      )}

      {!job.defaultEnabled && (
        <p className="mt-2 text-[11px] text-muted-foreground">
          Код-default = вимкнено: увімкнення потребує деплою (override може лише блокувати).
        </p>
      )}

      {job.states.length > 0 ? (
        <div className="mt-3 space-y-1.5">
          {job.states.map((st) => <StateRow key={st.watermarkKey} st={st} />)}
        </div>
      ) : (
        <p className="mt-3 text-xs text-muted-foreground">Ще не запускалась.</p>
      )}
    </div>
  );
}

function StateRow({ st }: { st: SyncJobState }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md bg-muted/40 px-3 py-1.5 text-xs">
      <span className="font-mono text-[11px] text-muted-foreground">{st.watermarkKey}</span>
      <StatusBadge status={st.status} />
      {st.nextDueAt && <span className="text-muted-foreground">наступний: {st.nextDueAt}</span>}
      {st.lastWatermark && <span className="text-muted-foreground">wm: {st.lastWatermark}</span>}
      {st.lockedBy && <span className="text-warning-foreground">lock: {st.lockedBy}</span>}
      {st.lastErrorCode && <span className="text-destructive">err: {st.lastErrorCode}</span>}
      {st.failureCount > 0 && <span className="text-destructive">×{st.failureCount}</span>}
    </div>
  );
}

function RunLogTable({ rows }: { rows: SyncRunLogEntry[] }) {
  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full text-xs">
        <thead className="bg-muted/50 text-muted-foreground">
          <tr className="text-left">
            <th className="px-3 py-2 font-medium">Початок</th>
            <th className="px-3 py-2 font-medium">Тип / Job</th>
            <th className="px-3 py-2 font-medium">Стан</th>
            <th className="px-3 py-2 font-medium text-right">C/U/S/F</th>
            <th className="px-3 py-2 font-medium">Snapshot</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.batchId} className="border-t">
              <td className="px-3 py-1.5 whitespace-nowrap text-muted-foreground">{r.startedAt ?? "—"}</td>
              <td className="px-3 py-1.5">
                <span className="font-medium">{r.syncType}</span>
                {r.jobId && <span className="text-muted-foreground"> · {r.jobId}</span>}
                {r.mode && <span className="text-muted-foreground"> · {r.mode}</span>}
              </td>
              <td className="px-3 py-1.5"><StatusBadge status={r.status} /></td>
              <td className="px-3 py-1.5 text-right font-mono whitespace-nowrap">
                {r.recordsCreated}/{r.recordsUpdated}/{r.recordsSkipped}/
                <span className={cn(r.recordsFailed > 0 && "text-destructive")}>{r.recordsFailed}</span>
              </td>
              <td className="px-3 py-1.5 font-mono text-[11px] text-muted-foreground">
                {r.snapshotId ?? "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// AD-1: картка фактичного потоку (read-only; дані = CSyncAudit + cron-розклад)
const FLOW_HEALTH: Record<SyncFactualFlow["health"], { label: string; cls: string }> = {
  ok: { label: "працює", cls: "bg-success text-success-foreground" },
  partial: { label: "частково", cls: "bg-warning text-warning-foreground" },
  failed: { label: "збій", cls: "bg-destructive text-destructive-foreground" },
  stale: { label: "застарілі дані", cls: "bg-warning text-warning-foreground" },
  never: { label: "ще не запускалась", cls: "bg-muted text-muted-foreground" },
};

function fmtDuration(ms: number | null): string | null {
  if (ms === null) return null;
  if (ms < 60_000) return `${Math.round(ms / 1000)} с`;
  return `${Math.round(ms / 60_000)} хв`;
}

function FactualFlowCard({ flow }: { flow: SyncFactualFlow }) {
  const h = FLOW_HEALTH[flow.health] ?? FLOW_HEALTH.never;
  const lr = flow.lastRun;
  const w = flow.window7d;
  return (
    <div
      className={cn(
        "rounded-lg border bg-card p-4",
        flow.health === "failed" && "border-destructive/40",
        (flow.health === "partial" || flow.health === "stale") && "border-warning-foreground/30",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium">{flow.label}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{flow.schedule}</p>
        </div>
        <span className={cn("shrink-0 rounded-badge px-2 py-0.5 text-xs", h.cls)}>{h.label}</span>
      </div>
      <div className="mt-2.5 space-y-1 text-xs">
        {lr ? (
          <>
            <p className="text-muted-foreground">
              Останній запуск: <span className="text-foreground">{lr.startedAt ?? "—"} UTC</span>
              {fmtDuration(lr.durationMs) && <> · {fmtDuration(lr.durationMs)}</>}
              {lr.processed > 0 && <> · опрацьовано {lr.processed.toLocaleString("uk-UA")}</>}
              {lr.failed > 0 && <span className="text-destructive"> · помилок {lr.failed}</span>}
            </p>
            {lr.status === "failed" && lr.error && (
              <p className="break-all rounded bg-destructive/5 px-2 py-1 font-mono text-[11px] text-destructive">
                {lr.error}
              </p>
            )}
          </>
        ) : (
          <p className="text-muted-foreground">Запусків ще не було.</p>
        )}
        {flow.nextDueLabel && (
          <p className="text-muted-foreground">Наступний за розкладом: {flow.nextDueLabel}</p>
        )}
        <p className="text-[11px] text-muted-foreground">
          За 7 днів: {w.ok} успішних{w.partial > 0 && <> · {w.partial} часткових</>}
          {w.failed > 0 && <span className="text-destructive"> · {w.failed} збоїв</span>}
        </p>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const variant: "success" | "destructive" | "muted" | "default" | "outline" =
    status === "success" || status === "idle"
      ? "success"
      : status === "running"
        ? "default"
        : status.startsWith("failed") || status === "partial"
          ? "destructive"
          : status === "blocked_by_dependency" || status === "needs_manual_review"
            ? "outline"
            : "muted";
  return <Badge variant={variant}>{status || "—"}</Badge>;
}

function ErrorPanel({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="rounded-lg border bg-destructive/5 p-6 text-center">
      <AlertCircle className="mx-auto mb-2 h-5 w-5 text-destructive" />
      <p className="text-sm text-destructive">{message}</p>
      <Button size="sm" variant="outline" className="mt-3 gap-1.5" onClick={onRetry}>
        <RotateCcw className="h-3.5 w-3.5" /> Спробувати ще
      </Button>
    </div>
  );
}
